import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, BUG1, BUG2, BUG3, BUG4, LESSON, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

// Small "BUG N / 5" counter chip used on every war-story scene.
const BugChip: React.FC<{ n: number; color: string }> = ({ n, color }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: 8,
      fontFamily: FONTS.mono,
      fontSize: 20,
      letterSpacing: "0.18em",
      color: COLORS.dim,
      border: `1px solid ${COLORS.line}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 8,
      padding: "8px 16px",
      marginBottom: 26,
    }}
  >
    <span style={{ color, fontWeight: 700, fontSize: 24 }}>BUG {n}</span>
    <span>/ 5</span>
  </div>
);

/* 01 — HOOK : always 22%, no error */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const pct = Math.round(
    interpolate(frame, [10, 55], [0, 22], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
  // after the climb, "freeze" pulse on the 22
  const stuck = frame > 60;
  const pulse = stuck ? 0.55 + 0.45 * Math.abs(Math.sin(frame / 7)) : 1;
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="5 production bugs · what broke" color={COLORS.red} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 280,
                lineHeight: 1,
                letterSpacing: "-0.03em",
                color: stuck ? COLORS.red : COLORS.text,
                opacity: pulse,
              }}
            >
              {pct}
            </span>
            <span
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 120,
                color: stuck ? COLORS.red : COLORS.dim,
                opacity: pulse,
              }}
            >
              %
            </span>
          </div>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 28,
              color: COLORS.dim,
              marginTop: 8,
            }}
          >
            sync hung here · no error · no crash · every time
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — BUG 1 : socket pool exhaustion (6/host) */
const SOCKET_PIPE: PipeNode[] = [
  { label: "queue.ts", value: "client", sub: "socket 1", accent: "cyan" },
  { label: "dedup.ts", value: "client", sub: "socket 2-5", accent: "cyan" },
  { label: "sync-worker", value: "client", sub: "socket 6", accent: "amber" },
  { label: "embeddings", value: "OpenRouter", sub: "no socket → HANG", accent: "red" },
];
export const Scene02Bug1: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.bug1.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <BugChip n={1} color={COLORS.cyan} />
      <Kicker text="socket pool exhaustion · maxSockets = 6 / host" color={COLORS.cyan} />
      <div style={{ width: 1660, marginBottom: 30 }}>
        <PipelineGraph nodes={SOCKET_PIPE} startFrame={8} stepFrames={24} nodeWidth={250} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 24,
          color: COLORS.dim,
        }}
      >
        every <span style={{ color: COLORS.text }}>createClient()</span> = a new keep-alive socket to Supabase
      </div>
    </AbsoluteFill>
    <Caption lines={BUG1} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — BUG 2 : stopword / acronym collision */
export const Scene03Bug2: React.FC = () => {
  const frame = useCurrentFrame();
  const corrupt = interpolate(frame, [200, 240], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug2.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <BugChip n={2} color={COLORS.amber} />
          <Kicker text="auto-acronyms collide with english" color={COLORS.amber} />
          <Panel accent="amber" title="query expander · before fix" width={1180}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 30, lineHeight: 1.9, color: COLORS.text }}>
              <div>
                What{" "}
                <span style={{ color: COLORS.amber, fontWeight: 700 }}>is</span>{" "}
                the Pareto principle?
              </div>
              <div style={{ color: COLORS.dim, fontSize: 24, margin: "10px 0" }}>
                ↳ "is" matches auto-acronym <span style={{ color: COLORS.amber }}>IS</span>
              </div>
              <div style={{ opacity: corrupt }}>
                What{" "}
                <span style={{ color: COLORS.red, fontWeight: 700 }}>is_superadmin</span>{" "}
                the Pareto principle?
              </div>
            </div>
          </Panel>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 24,
              color: COLORS.dim,
              marginTop: 24,
            }}
          >
            <span style={{ color: COLORS.red, fontWeight: 700 }}>3,158</span> junk acronyms derived from entity names — deleted
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={BUG2} accent="amber" />
    </AbsoluteFill>
  );
};

/* 04 — BUG 3 : MCP column mismatch */
export const Scene04Bug3: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.bug3.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <BugChip n={3} color={COLORS.magenta} />
        <Kicker text="mcp server · 500s · column does not exist" color={COLORS.magenta} />
        <Panel accent="magenta" title="src/lib/mcp/tools.ts · written from memory" width={1180}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 28, lineHeight: 2.0 }}>
            {[
              ["source_type", "provider_type"],
              ["last_synced_at", "last_sync_at"],
              ["chunk_index", "chunk_loc"],
              ["metadata", "metadata_graph"],
            ].map(([wrong, right]) => (
              <div key={wrong} style={{ display: "flex", alignItems: "center", gap: 18 }}>
                <span style={{ color: COLORS.red, textDecoration: "line-through", width: 280 }}>
                  {wrong}
                </span>
                <span style={{ color: COLORS.dim }}>→</span>
                <span style={{ color: COLORS.green, fontWeight: 700 }}>{right}</span>
              </div>
            ))}
          </div>
        </Panel>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.dim,
            marginTop: 22,
          }}
        >
          dev DB ≠ prod DB · verify against the migrations, not your memory
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={BUG3} accent="magenta" />
  </AbsoluteFill>
);

/* 05 — BUG 4 : strict-prompt refusal → decision tree */
const PROMPT_CODE = `// chat/completions/route.ts  — STRICT SOURCE MODE
// before: flat rules → "if sources don't contain the
//   answer, respond: not available."  ← LLM bails.

DECISION FRAMEWORK — for EVERY query:
  CASE A  same topic / system / feature area
          → answer, even with different wording
  CASE B  partially covered
          → answer the covered parts only
  CASE C  completely unrelated topics
          → only here: "not available"  (rare)

// Default to CASE A. Sources were search-matched
//   for THIS query — they are almost certainly relevant.`;
export const Scene05Bug4: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.bug4.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <BugChip n={4} color={COLORS.violet} />
      <Kicker text="perfect retrieval · llm refused to answer" color={COLORS.violet} />
      <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
        <Panel accent="red" title="the symptom" width={460} style={{ display: "flex" }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.5, color: COLORS.text }}>
            5 chunks · scores{" "}
            <span style={{ color: COLORS.green, fontFamily: FONTS.mono }}>0.91 0.88 0.87 0.85 0.82</span>
            <div style={{ marginTop: 18, color: COLORS.red, fontFamily: FONTS.mono, fontSize: 22 }}>
              LLM: "This information is not available."
            </div>
          </div>
        </Panel>
        <CodePanel
          code={PROMPT_CODE}
          language="tsx"
          filename="route.ts · the fix"
          highlightLines={[5, 6, 13]}
          revealFrames={260}
          fontSize={21}
          width={880}
        />
      </div>
    </AbsoluteFill>
    <Caption lines={BUG4} accent="violet" />
  </AbsoluteFill>
);

/* 06 — META-LESSON : tiny fix / hard diagnosis */
export const Scene06Lesson: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [40, 70], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what all five had in common" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 22 }}>
          <MetricCounter value={3} startFrame={10} durationFrames={45} accent="red" suffix=" days" fontSize={120} />
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={42} startFrame={20} durationFrames={50} accent="lime" suffix=" lines" fontSize={120} />
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.dim,
            marginTop: 10,
          }}
        >
          bug 1: 3-day diagnosis · 42-line singleton fix
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 40 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="lesson · the fix is tiny" accent="lime" width={400}>
              singleton · threshold · 4 renames · prompt tree
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="lesson · none show up in dev" accent="green" width={400}>
              only at scale, with <b style={{ color: COLORS.text }}>real users</b>
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={LESSON} accent="lime" />
    </AbsoluteFill>
  );
};

/* 07 — OUTRO */
export const Scene07Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Seniors don't avoid bugs.
          <br />
          <span style={{ color: COLORS.cyan }}>They find the root cause faster.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · built solo · war stories
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
